import { z } from "zod"

const plotSchema = z.object({
  kind: z.enum(["scatter", "bar", "line"]),
  title: z.string().optional(),
  caption: z.string().optional(),
  xLabel: z.string().optional(),
  yLabel: z.string().optional(),
  leftLabel: z.string().optional(),
  rightLabel: z.string().optional(),
  topLabel: z.string().optional(),
  bottomLabel: z.string().optional(),
  points: z
    .array(
      z.object({
        label: z.string(),
        x: z.number().finite(),
        y: z.number().finite(),
      }),
    )
    .min(1)
    .max(100),
})

const chartNumber = new Intl.NumberFormat("en-US", {
  maximumSignificantDigits: 3,
})

export function NotebookPlot({ spec: input }: { spec: unknown }) {
  const parsed = plotSchema.safeParse(input)
  if (!parsed.success) {
    return (
      <p className="my-5 text-coval-text-muted">
        This chart has invalid data. Ask Sofia to recreate it.
      </p>
    )
  }
  const spec = parsed.data
  const points = spec.points
  const quadrant =
    spec.kind === "scatter" && !!(spec.leftLabel || spec.rightLabel)
  const xs = points.map((p) => p.x),
    ys = points.map((p) => p.y)
  const minX = quadrant
      ? 0
      : spec.kind === "line" && xs.length
        ? Math.min(...xs)
        : Math.min(0, ...xs),
    maxX = quadrant
      ? 100
      : spec.kind === "line" && xs.length
        ? Math.max(...xs) > minX
          ? Math.max(...xs)
          : minX + 1
        : Math.max(1, ...xs)
  const minY = quadrant ? 0 : Math.min(0, ...ys),
    maxY = quadrant ? 100 : Math.max(1, ...ys)
  const x = (value: number) => 80 + ((value - minX) / (maxX - minX)) * 1040
  const y = (value: number) => 350 - ((value - minY) / (maxY - minY)) * 270
  return (
    <figure className="my-5" aria-label={spec.title ?? "Chart"}>
      {spec.title && (
        <figcaption className="mb-3 font-semibold">{spec.title}</figcaption>
      )}
      <svg
        viewBox="0 0 1200 440"
        className="w-full"
        role="img"
        aria-label={spec.caption ?? spec.title ?? "Chart"}
      >
        <line
          x1="80"
          y1="350"
          x2="1120"
          y2="350"
          stroke="currentColor"
          opacity=".5"
        />
        <line
          x1="80"
          y1="80"
          x2="80"
          y2="350"
          stroke="currentColor"
          opacity=".5"
        />
        {quadrant && (
          <g stroke="currentColor" opacity=".25" strokeDasharray="5 5">
            <line x1="600" y1="80" x2="600" y2="350" />
            <line x1="80" y1="215" x2="1120" y2="215" />
          </g>
        )}
        <g fill="currentColor" fontSize="28">
          <text x="600" y="35" textAnchor="middle">
            {spec.topLabel ?? spec.yLabel}
          </text>
          <text x="600" y="420" textAnchor="middle">
            {spec.bottomLabel ?? spec.xLabel}
          </text>
          {spec.kind === "scatter" && (
            <>
              <text x="80" y="385">
                {spec.leftLabel ?? minX}
              </text>
              <text x="1120" y="385" textAnchor="end">
                {spec.rightLabel ?? maxX}
              </text>
            </>
          )}
          {!quadrant &&
            [...new Set([minY, 0, maxY])].map((value) => (
              <text key={value} x="66" y={y(value) + 7} textAnchor="end">
                {chartNumber.format(value)}
              </text>
            ))}
        </g>
        {spec.kind === "line" && (
          <polyline
            points={points.map((p) => `${x(p.x)},${y(p.y)}`).join(" ")}
            fill="none"
            stroke="var(--coval-chart-mark-1)"
            strokeWidth="3"
          />
        )}
        {points.map((point, i) => (
          <g key={`${point.label}:${i}`}>
            {spec.kind === "bar" ? (
              <rect
                x={90 + (i * 1020) / points.length}
                y={Math.min(y(point.y), y(0))}
                width={Math.max(4, 940 / points.length)}
                height={Math.abs(y(0) - y(point.y))}
                fill={`var(--coval-chart-${spec.kind === "bar" ? "series" : "mark"}-${(i % 8) + 1})`}
              />
            ) : (
              <circle
                cx={x(point.x)}
                cy={y(point.y)}
                r="6"
                fill={`var(--coval-chart-mark-${spec.kind === "line" ? 1 : (i % 8) + 1})`}
              />
            )}
            <text
              x={
                spec.kind === "bar"
                  ? 90 + (i * 1020 + 470) / points.length
                  : spec.kind === "line"
                    ? x(point.x)
                    : x(point.x) + (x(point.x) > 920 ? -9 : 9)
              }
              y={
                spec.kind !== "scatter" ? 390 : y(point.y) + (i % 2 ? 24 : -12)
              }
              textAnchor={
                spec.kind !== "scatter"
                  ? "middle"
                  : x(point.x) > 920
                    ? "end"
                    : "start"
              }
              fontSize={spec.kind === "scatter" ? 22 : 28}
              fill="currentColor"
            >
              {point.label}
            </text>
            {spec.kind !== "scatter" && (
              <text
                x={
                  spec.kind === "bar"
                    ? 90 + (i * 1020 + 470) / points.length
                    : x(point.x)
                }
                y={y(point.y) + (point.y < 0 ? 24 : -12)}
                textAnchor="middle"
                fontSize="28"
                fill="currentColor"
              >
                {chartNumber.format(point.y)}
              </text>
            )}
            <title>
              {point.label}: {point.x}, {point.y}
            </title>
          </g>
        ))}
      </svg>
      {spec.caption && <p className="text-sm">{spec.caption}</p>}
    </figure>
  )
}
